import { useState } from 'react'
import { config } from '../lib/config'
import { acledPosts, acledGenerated, acledWindowLabel, NEWS_DAYS } from '../lib/acled'
import { demoStore } from '../features/demo/store'
import type { AcledPost } from '../types/acled'
import type { PostRow } from '../types/db'
import { PostCard } from '../components/PostCard'
import { EmptyState } from '../components/States'
import { FlagDialog } from '../components/FlagDialog'

const acledToPost = (p: AcledPost, index: number): PostRow => ({
  id: `acled-${index}`,
  forum_id: 'overview',
  author: null,
  title: p.title,
  body: p.body,
  flair: p.fat > 3 ? 'FLASH' : 'VERIFIED',
  severity: p.fat > 3 ? 1 : 2,
  region: `${p.country} · ${p.admin1}`,
  source: p.src,
  confidence: 0.72 + (index % 3) * 0.06,
  votes: p.fat * 5 + 12,
  status: 'verified',
  is_first_report: index === 3,
  media: [],
  created_at: p.date,
})

export function BlogPage() {
  const [flag, setFlag] = useState<PostRow | null>(null)
  const [filter, setFilter] = useState('All')
  // Demo-Daten nur im E2E-Modus (Playwright); Production zeigt ausschließlich echte ACLED-Daten.
  const community = config.e2e ? demoStore.posts() : []
  // News-Regel: maximal NEWS_DAYS Tage zurück
  const acled = acledPosts().map(acledToPost)
  const all = [...community, ...acled]
  const visible =
    filter === 'All'
      ? all
      : all.filter((p) =>
          filter === 'Flash'
            ? p.flair === 'FLASH'
            : filter === 'Verified'
              ? p.status === 'verified'
              : p.region?.includes(filter ?? ''),
        )

  const flash = acled.filter((p) => p.flair === 'FLASH').length
  const verified = acled.filter((p) => p.status === 'verified').length
  const regions = new Set(acled.map((p) => p.region?.split('·')[0]?.trim()).filter(Boolean)).size
  const total = acled.length
  const windowLabel = acledWindowLabel()
  const generated = acledGenerated()

  return (
    <div className="page brief-page">
      <section className="brief-head">
        <span className="eyebrow">— LIVE BRIEF</span>
        <h1>Global conflict — situation report</h1>
        <p>
          ACLED · LAST {NEWS_DAYS} DAYS · WINDOW {windowLabel} · GENERATED {generated} ·
          ACLED + GUILD SOURCES
        </p>
      </section>
      <section className="brief-stats" aria-label="Brief statistics">
        <article>
          <strong>{flash}</strong>
          <span>FLASH</span>
        </article>
        <article>
          <strong>{total}</strong>
          <span>EVENTS IN WINDOW</span>
        </article>
        <article>
          <strong>{verified}</strong>
          <span>VERIFIED</span>
        </article>
        <article>
          <strong>{regions}</strong>
          <span>REGIONS</span>
        </article>
      </section>
      <div className="filter-chips" aria-label="Filter reports">
        {['All', 'Flash', 'Verified', 'Ukraine', 'Russia'].map((item) => (
          <button
            className={filter === item ? 'active' : ''}
            onClick={() => setFilter(item)}
            key={item}
          >
            {item}
          </button>
        ))}
      </div>
      <section aria-label="Intelligence reports">
        {visible.map((post) => (
          <PostCard
            key={post.id}
            post={post}
            source={post.id.startsWith('acled') ? 'acled' : 'community'}
            onFlag={post.id.startsWith('acled') ? undefined : setFlag}
          />
        ))}
        {visible.length === 0 && (
          <EmptyState
            title="No news in the last 3 days"
            message={`The current ACLED data window is ${windowLabel} (generated ${generated}). The news feed only shows events from the last ${NEWS_DAYS} days — as soon as fresher data is available it appears here automatically.`}
          />
        )}
      </section>
      {flag && <FlagDialog postId={flag.id} onClose={() => setFlag(null)} />}
    </div>
  )
}
